import { Injectable, inject } from '@angular/core';
import { NotificationService } from '../services/notification.service';
import { MessagingThreadsService } from '../messaging/messaging-threads.service';
import { FirebaseAlertService } from './services/firebase-alert.service';
import { AuthService } from './auth/auth.service';
import { HttpClient } from '@angular/common/http';
import { backendEndPointToken } from '../app.config';
import { Bundle, Appointment } from 'fhir/r4';
import { of, forkJoin } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class UnifiedNotificationService {
  private notificationService = inject(NotificationService);
  private messagingThreadsService = inject(MessagingThreadsService);
  private firebaseAlertService = inject(FirebaseAlertService);
  private auth = inject(AuthService);
  private http = inject(HttpClient);
  private backendUrl = inject(backendEndPointToken);

  broadcastEmergency(description: string, title = 'Top Level Emergency Declared!', url = '/app/triage') {
    const orgId = this.auth.getOrganizationId?.() || '';
    if (!orgId) return;

    this.http.get<any>(`${this.backendUrl}/Practitioner?active=true&_count=500`).subscribe({
      next: (bundle: any) => {
        const entries = bundle.entry || [];
        for (const entry of entries) {
          const practId = entry.resource?.id;
          if (practId) {
            const alert = {
              title,
              description,
              url,
              type: 'emergency'
            };
            this.firebaseAlertService.postUserAlert(orgId, practId, alert).subscribe({
              error: () => {}
            });
          }
        }
      },
      error: (err: any) => {
        console.error('Failed to broadcast emergency:', err);
      }
    });
  }

  /**
   * Notify about cancelled appointments using all three channels.
   * - Creates a role/system notification via NotificationService
   * - Ensures messaging threads and posts a notify message via patient backend
   * - Posts realtime alerts to Firebase (user + role paths)
   * responseBundle is optional; if provided we try to find created CommunicationRequest IDs.
   */
  notifyAppointmentCancellations(appointments: any[], responseBundle?: Bundle) {
    const orgId = this.auth.getOrganizationId?.() || '';

    for (const appt of appointments) {
      const appointmentId = appt?.id || '';
      const patientRef = (appt?.participant ?? []).map((p: any) => p.actor?.reference).find((r: string) => String(r || '').startsWith('Patient/')) || null;
      const practitionerRefs = (appt?.participant ?? []).map((p: any) => p.actor?.reference).filter(Boolean) as string[];

      const patientParticipant = (appt?.participant ?? []).find((p: any) => String(p.actor?.reference || '').startsWith('Patient/'));
      const patientName = patientParticipant?.actor?.display || 'the patient';
      
      const practitionerParticipants = (appt?.participant ?? []).filter((p: any) => String(p.actor?.reference || '').startsWith('Practitioner/'));
      const practitionerNames = practitionerParticipants.map((p: any) => p.actor?.display || 'a practitioner').join(' and ') || 'the practitioner';
      
      const dateObj = appt?.start ? new Date(appt.start) : null;
      const dateStr = dateObj ? dateObj.toLocaleDateString() : 'an unknown date';
      const timeStr = dateObj ? dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'an unknown time';
      
      const detailMessage = `The appointment scheduled on ${dateStr} at ${timeStr} between ${practitionerNames} and ${patientName} has been cancelled.`;

      // 1) Trigger targeted system notification
      try {
        const userIds: string[] = [];
        if (patientRef) userIds.push(String(patientRef).split('/')[1]);
        for (const r of practitionerRefs) {
          const pId = String(r).split('/')[1];
          if (pId) userIds.push(pId);
        }

        const targetedRequest = {
          UserIds: userIds,
          Title: 'Appointment cancelled',
          Message: detailMessage,
          SendEmail: true,
          ResourceType: 'Appointment',
          ResourceId: appointmentId
        };
        this.notificationService.triggerTargetedNotification(targetedRequest).pipe(catchError(() => of(null))).subscribe();
      } catch (e) {
        // swallow
      }

      // 2) Notify messaging threads (patient backend)
      try {
        const userId = this.auth.user.getValue()?.id || 'system';
        const participants = [] as any[];
        if (patientRef) {
          participants.push({ reference: patientRef, participantId: String(patientRef).split('/')[1], resourceId: String(patientRef).split('/')[1] });
        }
        for (const r of practitionerRefs) {
          participants.push({ reference: r, participantId: String(r).split('/')[1], resourceId: String(r).split('/')[1] });
        }

        const ensurePayload = {
          organizationId: orgId,
          threadType: 'appointment',
          sender: { resourceType: 'System', participantId: userId, resourceId: userId },
          participants,
          subject: { reference: appointmentId ? `Appointment/${appointmentId}` : undefined },
          title: `Appointment cancelled`
        };

        this.messagingThreadsService.ensureThread(ensurePayload as any).pipe(catchError(() => of(null))).subscribe((res: any) => {
          const threadId = res?.thread?.threadId;
          if (!threadId) return;
          const messagePayload = {
            organizationId: orgId,
            sender: { resourceType: 'System', participantId: userId, resourceId: userId },
            context: {},
            subject: { reference: appointmentId ? `Appointment/${appointmentId}` : undefined },
            message: { text: detailMessage }
          } as any;
          this.messagingThreadsService.notifyThreadMessage(threadId, messagePayload).pipe(catchError(() => of(null))).subscribe();
        });
      } catch (e) {
        // swallow
      }

      // 3) Firebase realtime alerts
      try {
        const alert = { title: 'Appointment cancelled', description: detailMessage, url: '/app/appointments', query: { appointmentId } } as any;
        if (patientRef) {
          const patientId = String(patientRef).split('/')[1];
          if (patientId) this.firebaseAlertService.postUserAlert(orgId, patientId, alert).pipe(catchError(() => of(null))).subscribe();
        }
        // post a role alert for practitioners involved
        for (const r of practitionerRefs) {
          // best-effort: try to extract practitioner id
          const roleAlert = { ...alert, title: 'Practitioner notified' } as any;
          const practitionerId = String(r).split('/')[1];
          if (practitionerId) this.firebaseAlertService.postUserAlert(orgId, practitionerId, roleAlert).pipe(catchError(() => of(null))).subscribe();
        }
      } catch (e) {
        // swallow
      }
    }
  }
}
